import {
    createSlice,
    createAsyncThunk,
    Dispatch,
    ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import { APIClient } from '../../services/clientAPI';
import { userSelectionActions } from './userSelectionReducer';
import { User } from './authReducer';
import { Facility as FacilityDbApi } from '@liveline/ll_node_common/lib/models/api/facility';

const name = 'facilityList';

export interface Facility extends FacilityDbApi {
    value?: number; //customer facilityId
    label?: string;
    text?: string;
}

export interface FacilityListState {
    data: Facility[];
    loading: boolean;
}

function createInitialState(): FacilityListState {
    const stored = localStorage.getItem(name) || '[]';
    const data = JSON.parse(stored);
    return {
        data,
        loading: false,
    };
}

const initLivelineFacilitySelection = (
    dispatch: Dispatch,
    selectedFacility: any,
) => {
    dispatch(userSelectionActions.setUserSelection(selectedFacility));
};

async function facilityFetch(apiUrl: string, thunkAPI: any) {
    if (apiUrl) {
        const client = new APIClient(apiUrl, thunkAPI.dispatch);
        const response: FacilityDbApi[] | undefined =
            await client.getAllFacilities();
        let facilities =
            response && response?.length
                ? response.map((facility: FacilityDbApi) => {
                      return {
                          ...facility,
                          text: facility?.facilityName,
                          value: facility?.id,
                          label: facility?.facilityName,
                      } as Facility;
                  })
                : [];

        // Store facility list in local storage
        localStorage.setItem(name, JSON.stringify(facilities));

        const stored = localStorage.getItem('user') || '{}';
        const user: User = JSON.parse(stored);

        if (user?.permissions) {
            facilities = facilities.filter(function ({
                livelineFacilityId,
            }) {
                return (
                    (user?.permissions || []).findIndex(
                        (p) => p.facilityId === livelineFacilityId,
                    ) > -1
                );
            });
        }
        // Set default facility selection to first facility in list
        if (facilities.length) {
            initLivelineFacilitySelection(thunkAPI.dispatch, {
                facility: facilities[0],
            });
        }

        return facilities;
    }

    return [];
}

export const fetchFacilitiesList = createAsyncThunk(
    `${name}/fetch`,
    facilityFetch,
);

export const slice = createSlice({
    name,
    initialState: createInitialState(),
    reducers: {
        setFacilityList: (state, action) => {
            // Update Facility List
            return action.payload;
        },
        logout: (state) => {
            state.data = [];
            localStorage.removeItem(name);
        },
    },
    extraReducers: (
        builder: ActionReducerMapBuilder<FacilityListState>,
    ) => {
        builder
            .addCase(
                fetchFacilitiesList.fulfilled,
                (state, action) => {
                    return {
                        loading: false,
                        data: action.payload || [],
                    };
                },
            )
            .addCase(fetchFacilitiesList.pending, (state) => ({
                loading: true,
                data: state.data,
            }))
            .addCase(fetchFacilitiesList.rejected, () => ({
                loading: false,
                data: [],
            }));
    },
});

// Action creator
export const facilityListActions = slice.actions;

export const facilityListReducer = slice.reducer;
